/**
 * #1316 — at an identity or account end, drop the react-query entries that belong to the previous session. Nothing is
 * refetched inside the listener (a listener must not send a request — see `endSession`): `SessionScope` remounts the UI
 * on the new epoch, and its queries start fresh. The one refetch is deferred a tick, after an identity SWITCH only: the
 * active `EPOCH_KEYS` queries outside the decks, once the new headers are applied. Identity-scoped keys are recognised by `keyIdentityOf`; the lists below are the
 * per-person / per-account keys that carry no identity in the key.
 */
import type { Query, QueryClient } from '@tanstack/react-query'
import { ACCOUNT_REASONS, EPOCH_REASONS, onSessionEnd } from '@papan/core-ui/ui/sessionEnded'
import { keyIdentityOf } from '@papan/files/client/queries'

/** Removed on an ACCOUNT end only (`MobileShell.tsx`, `useMyCapabilities.ts`): the same account keeps them across an identity switch. */
const ACCOUNT_KEYS: ReadonlyArray<readonly unknown[]> = [['mobile-my-tenants'], ['mobile-my-invites'], ['platform-capabilities']]
/** Removed on EVERY epoch end: per-person data under keys without the identity (`useBusySessions`, `useChatHomeSessions`, `useOrgCreateLimit`). */
const EPOCH_KEYS: ReadonlyArray<readonly unknown[]> = [['agent', 'busy-sessions'], ['agent', 'home-recent-sessions'], ['org-create-limit']]

export function installSessionQueryReset(queryClient: QueryClient): () => void {
  return onSessionEnd((reason) => {
    if (!EPOCH_REASONS.has(reason)) return
    // Review round 1 (B-1): a query still FETCHING is not removed now — removing it cancels its fetch, and its caller
    // gets a `CancelledError` no re-ask recognises (the workspace tree's `loadFailed`, a pending chain read as
    // approved). It settles first (its answer dropped as session-ended), then goes.
    const cache = queryClient.getQueryCache()
    for (const q of cache.findAll({ predicate: (q) => keyIdentityOf(q.queryKey) !== null })) {
      if (q.state.fetchStatus === 'fetching') removeWhenSettled(queryClient, q)
      else cache.remove(q)
    }
    // Mounted observers (outside the remounted decks) keep a removed Query object and its old data, so these keys are
    // RESET in place (back to pending, no data, no request) instead of removed.
    const keys = ACCOUNT_REASONS.has(reason) ? [...EPOCH_KEYS, ...ACCOUNT_KEYS] : EPOCH_KEYS
    for (const queryKey of keys) for (const q of queryClient.getQueryCache().findAll({ queryKey })) q.reset()
    // An identity switch refetches the active ones once its new headers are applied (next tick). An ACCOUNT end never
    // does: mid sign-out the cookie is about to clear and a refetch could 401 into a toast on the login page; after an
    // account change the tree re-renders with the new user and its observers rebuild on render / focus.
    if (reason === 'identity-switched') {
      setTimeout(() => {
        for (const queryKey of EPOCH_KEYS) void queryClient.refetchQueries({ queryKey, type: 'active' })
      }, 0)
    }
  })
}

function removeWhenSettled(queryClient: QueryClient, query: Query): void {
  const cache = queryClient.getQueryCache()
  const off = cache.subscribe((event) => {
    if (event.query !== query) return
    if (event.type === 'removed') { off(); return }
    if (query.state.fetchStatus === 'fetching') return
    off()
    if (cache.get(query.queryHash) === query) cache.remove(query)
  })
}
